import React, { useState, useEffect } from 'react'
import {
  QrCodeIcon,
  BanknotesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { API_BASE_URL } from '@/services/api'

interface Account {
  id: number
  name: string
  balance: number
  bank: string
  type: string
}

interface Transfer {
  id: string
  status: string
  amount: number
  pix_key: string
  pix_key_type: string
  description: string
  created_at: string
  transfer_date?: string
}

const PIXTransferPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showTokenField, setShowTokenField] = useState(false)
  const [tokenRequired, setTokenRequired] = useState(false)
  const { token } = useAuthStore()

  const [formData, setFormData] = useState({
    account_id: '',
    pix_key: '',
    pix_key_type: 'CPF',
    amount: '',
    description: '',
    schedule_date: '',
    validation_token: ''
  })

  useEffect(() => {
    fetchAccounts()
    fetchTransfers()
  }, [])

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Filtrar apenas contas de gateway (ASAAS, PAGARME, STRIPE)
        const gatewayAccounts = (data?.accounts || []).filter((acc: Account) =>
          acc.bank && ['ASAAS', 'PAGARME', 'STRIPE'].includes(acc.bank.toUpperCase())
        )
        setAccounts(gatewayAccounts)
      }
    } catch (err) {
      console.error('Erro ao buscar contas:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchTransfers = async () => {
    // Aqui você pode buscar o histórico de transferências PIX da API
    // Por enquanto, vazio
    setTransfers([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setSending(true)

    try {
      const payload = {
        pix_key: formData.pix_key,
        pix_key_type: formData.pix_key_type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        schedule_date: formData.schedule_date || null,
        validation_token: formData.validation_token || null
      }

      console.log('🚀 Enviando PIX transfer:', payload)

      const response = await fetch(
        `${API_BASE_URL}/accounts/${formData.account_id}/pix-transfer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      )

      const result = await response.json()
      console.log('📩 Resposta do servidor:', result)

      if (!response.ok) {
        console.error('❌ Erro HTTP', response.status, result)
        throw new Error(result.detail || JSON.stringify(result) || 'Erro ao enviar PIX')
      }

      setSuccess(`✅ PIX enviado com sucesso! ID: ${result.transfer?.transfer_id}`)

      // Limpar formulário
      setFormData({
        account_id: formData.account_id, // Manter a conta selecionada
        pix_key: '',
        pix_key_type: 'CPF',
        amount: '',
        description: '',
        schedule_date: '',
        validation_token: ''
      })

      // Resetar estados de token
      setShowTokenField(false)
      setTokenRequired(false)

      // Atualizar lista de transferências
      fetchTransfers()

    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao enviar PIX'

      // Verificar se é erro pedindo token de validação
      if (errorMessage.includes('token') || errorMessage.includes('validação') || errorMessage.includes('validation')) {
        setShowTokenField(true)
        setTokenRequired(true)
        setError('🔐 Token de validação necessário! Um código foi enviado para seu celular/app. Por favor, insira-o abaixo.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setSending(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatPixKey = (key: string, type: string) => {
    if (type === 'CPF') {
      return key.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
    if (type === 'PHONE') {
      return key.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    return key
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center">
            <QrCodeIcon className="w-9 h-9 mr-3" />
            Transferência PIX
          </h1>
          <p className="text-blue-100">
            Envie PIX instantaneamente para qualquer chave via gateway de pagamento
          </p>
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div className={`${tokenRequired ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'} border rounded-xl p-4 flex items-start`}>
          {tokenRequired ? (
            <InformationCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
          ) : (
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
          )}
          <div className="flex-1">
            <h3 className={`${tokenRequired ? 'text-yellow-800' : 'text-red-800'} font-semibold`}>
              {tokenRequired ? 'Token de Validação Necessário' : 'Erro ao enviar PIX'}
            </h3>
            <p className={`${tokenRequired ? 'text-yellow-700' : 'text-red-600'} text-sm mt-1`}>{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-green-800 font-semibold">PIX enviado!</h3>
            <p className="text-green-600 text-sm mt-1">{success}</p>
          </div>
        </div>
      )}

      {accounts.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start">
          <InformationCircleIcon className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-yellow-800 font-semibold">Nenhuma conta de gateway encontrada</h3>
            <p className="text-yellow-600 text-sm mt-1">
              Configure um gateway de pagamento (ASAAS, PAGARME ou STRIPE) para enviar PIX.
            </p>
          </div>
        </div>
      )}

      {/* Formulário de Transferência */}
      {accounts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 text-white">
            <h3 className="text-xl font-semibold flex items-center">
              <BanknotesIcon className="w-6 h-6 mr-2" />
              Nova Transferência PIX
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Seleção de Conta */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Conta de Débito
              </label>
              <select
                value={formData.account_id}
                onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              >
                <option value="">Selecione a conta para debitar</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.bank}) - Saldo: {formatCurrency(account.balance)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipo de Chave */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Chave PIX
                </label>
                <select
                  value={formData.pix_key_type}
                  onChange={(e) => setFormData({...formData, pix_key_type: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                  <option value="EMAIL">E-mail</option>
                  <option value="PHONE">Telefone</option>
                  <option value="EVP">Chave Aleatória</option>
                </select>
              </div>

              {/* Chave PIX */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Chave PIX Destino
                </label>
                <input
                  type="text"
                  value={formData.pix_key}
                  onChange={(e) => setFormData({...formData, pix_key: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={
                    formData.pix_key_type === 'CPF' ? '000.000.000-00' :
                    formData.pix_key_type === 'EMAIL' ? 'email@exemplo.com' :
                    formData.pix_key_type === 'PHONE' ? '(11) 99999-9999' :
                    'Digite a chave PIX'
                  }
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Valor */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Valor
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                    R$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full border-2 border-gray-300 rounded-lg pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>

              {/* Data Agendamento (opcional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Agendar para (opcional)
                </label>
                <input
                  type="date"
                  value={formData.schedule_date}
                  onChange={(e) => setFormData({...formData, schedule_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe em branco para transferência imediata
                </p>
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descrição (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                rows={3}
                placeholder="Motivo da transferência..."
              />
            </div>

            {/* Token de Validação - Aparece apenas quando solicitado */}
            {showTokenField && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 animate-fadeIn">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <label className="block text-sm font-bold text-yellow-900 mb-2">
                      🔐 Token de Validação (SMS/APP) *
                    </label>
                    <input
                      type="text"
                      value={formData.validation_token}
                      onChange={(e) => setFormData({...formData, validation_token: e.target.value})}
                      className="w-full border-2 border-yellow-400 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all bg-white text-lg font-mono"
                      placeholder="Digite o código recebido"
                      autoFocus
                      required
                    />
                    <p className="text-xs text-yellow-800 mt-2 font-medium">
                      📱 Um código de validação foi enviado para seu celular/app. Digite-o acima e clique em "Enviar PIX" novamente.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Resumo da Transferência */}
            {formData.account_id && formData.amount && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <InformationCircleIcon className="w-5 h-5 mr-2" />
                  Resumo da Transferência
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conta de débito:</span>
                    <span className="font-medium text-gray-900">
                      {accounts.find(a => a.id.toString() === formData.account_id)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Chave PIX destino:</span>
                    <span className="font-medium text-gray-900">
                      {formData.pix_key || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Valor:</span>
                    <span className="font-bold text-blue-600 text-lg">
                      {formatCurrency(parseFloat(formData.amount) || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tipo:</span>
                    <span className="font-medium text-gray-900">
                      {formData.schedule_date ? '📅 Agendado' : '⚡ Instantâneo'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={sending}
                className={`flex-1 ${showTokenField ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'} text-white px-6 py-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg flex items-center justify-center transition-all shadow-lg hover:shadow-xl`}
              >
                {sending ? (
                  <>
                    <ClockIcon className="w-6 h-6 mr-2 animate-spin" />
                    {showTokenField ? 'Validando...' : 'Enviando...'}
                  </>
                ) : (
                  <>
                    <ArrowRightIcon className="w-6 h-6 mr-2" />
                    {showTokenField ? '🔐 Confirmar com Token' : 'Enviar PIX'}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    account_id: '',
                    pix_key: '',
                    pix_key_type: 'CPF',
                    amount: '',
                    description: '',
                    schedule_date: '',
                    validation_token: ''
                  })
                  setError(null)
                  setSuccess(null)
                  setShowTokenField(false)
                  setTokenRequired(false)
                }}
                className="bg-gray-500 text-white px-6 py-4 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
              >
                Limpar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Histórico de Transferências */}
      {transfers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Histórico de Transferências</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chave PIX</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transfer.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatPixKey(transfer.pix_key, transfer.pix_key_type)}
                      </div>
                      <div className="text-sm text-gray-500">{transfer.pix_key_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(transfer.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {transfer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transfer.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default PIXTransferPage
